





























































































import {
  DrivewealthAccountManagementType,
  DrivewealthTransactionUnit,
  InstrumentDetail,
  InstrumentStatus
} from "@/shared/types";
import { Component, Prop, Vue, Watch } from "@/shared/vuepack";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import RiskDisclaimer from "@/shared/components/investments/drivewealth/RiskDisclaimer.vue";
import SharePriceTable from "@/employee/components/investments/drivewealth/SharePriceTable.vue";
import { formatCurrency } from "@/shared/currency";
import * as RouteNames from "@/employee/route-names";
import * as SharedRoutes from "@/shared/route-names";
import { Validator } from "vee-validate";
import { useNotifierStore } from "@/shared/store/notifier";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserStore } from "@/shared/store/user";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

interface RequestOrderData {
  shouldSellAll: boolean;
  amountUsd: number;
  isSubmitting: boolean;
}

@Component({
  computed: {
    DrivewealthTransactionUnit () {
      return DrivewealthTransactionUnit;
    },
  },
  components: {
    GenericLoadingSpinner,
    SharePriceTable,
    RiskDisclaimer,
  }
})
export default class RequestBuyPage extends Vue {
  @Prop() symbol: string;

  @Prop() accountId: number;

  data?: RequestOrderData = null;
  loadedStockSymbol?: string = null;
  selectedTransactionUnit = DrivewealthTransactionUnit.ByAmount;
  amountShares = 0;

  drivewealthStore = useDrivewealthStore();
  notifierStore = useNotifierStore();
  featureFlagStore = useFeatureFlagStore();
  userStore = useUserStore();
  custodianSettingsStore = useCustodianSettingsStore();


  formatCurrency = formatCurrency;
  $validator: Validator;

  created (): void {
    this.drivewealthStore.getAccountSummaryV2({ username: null, partnerId: null, dwAccountId: this.accountId });
    this.onCreatedAndLoaded();
    this.userStore.loadPartnerSettings();
    this.drivewealthStore.loadHsaBalanceAndThreshold({ force: true, accountId: this.accountId });
  }

  get isManagedAccount (): boolean {
    return this.drivewealthStore.isAccountManagementType(
        DrivewealthAccountManagementType.RiaManaged, this.accountId);
  }

  get isLoading (): boolean {
    return !this.isLoaded;
  }

  get portalFunctionalityDisabled (): boolean {
    return this.custodianSettingsStore.portalFunctionalityDisabled;
  }

  get instrumentDetail (): InstrumentDetail {
    return this.drivewealthStore.instrumentDetailBySymbol(this.symbol);
  }

  get selectDrivewealthTransactionUnitEnabled (): boolean {
    return this.featureFlagStore.flags.bend24349EnableBuySellByShare  &&
        !this.isManagedAccount;
  }

  get submitButtonDisabled (): boolean {
    if (this.portalFunctionalityDisabled) {
      return true;
    }
    return this.disableAmount || this.inputAmountExceedsBalance || this.data.isSubmitting;
  }

  get isLoaded (): boolean {
    const accountSummaryV2Loaded = this.drivewealthStore.accountSummaryV2Loaded(this.accountId);

    const instrumentDataLoaded = this.isManagedAccount ? true : this.drivewealthStore.instrumentDataIsLoaded(this.symbol);
    return !!this.data &&
        this.drivewealthStore.isHsaBalanceAndThresholdLoaded(this.accountId) &&
        instrumentDataLoaded &&
        accountSummaryV2Loaded;
  }

  @Watch("selectedTransactionUnit")
  resetInputOnUnitChange (): void {
    this.amountShares = 0;
    this.data.amountUsd = 0;
  }

  @Watch("symbol")
  getDataOnSymbolChange (): void {
    if (this.data && this.loadedStockSymbol !== this.symbol) {
      this.data = null; // the symbol changed, gotta start over!
      this.onCreatedAndLoaded(); // N.B. Setting data = null stops this from being infinite loop
    }

    this.loadedStockSymbol = this.symbol;
    this.data = {
      shouldSellAll: false,
      amountUsd: 0,
      isSubmitting: false
    };
  }

  @Watch("isLoaded")
  onCreatedAndLoaded (): void {
    if (!this.isManagedAccount) {
      this.drivewealthStore.loadInstrumentData(this.symbol, this.accountId,
          { username: null, partnerId: null });
    }

    this.getDataOnSymbolChange();
  }

  get heading (): string {
    return this.drivewealthStore.instrumentHeading(this.symbol, this.accountId);
  }

  get subheading (): string {
    return this.drivewealthStore.instrumentSubheading(this.symbol, this.accountId);
  }

  get availableToInvest (): number {
    return this.drivewealthStore.isHsaBalanceAndThresholdLoaded(this.accountId)
        ? this.drivewealthStore.hsaBalanceBeyondThreshold(this.accountId) : 0;
  }

  get hasPendingManagedLiquidation (): boolean {
    const pendingActivity = this.drivewealthStore.pendingActivityById(this.accountId);

    return this.isManagedAccount && pendingActivity.hasPendingManagedLiquidation;
  }

  get canBuy (): boolean {
    if (this.userStore.partnerDisableInvestmentActivity) {
      return false;
    }

    if (this.isManagedAccount) {
      return !this.hasPendingManagedLiquidation;
    }

    return this.instrumentDetail && this.instrumentDetail.status === InstrumentStatus.Active;
  }

  get disableAmount (): boolean {
    return !this.canBuy || this.insufficientFundsForBuy;
  }

  get minAmount (): number {
    return 1; // must buy at least $1.00
  }

  get amountValidator () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: this.minAmount,
      max_value_usd: this.availableToInvest
    };
  }

  get sharesValidator () {
    return {
      required: true,
      decimal: 2,
      min_value: 0.1,
      max_value: (this.availableToInvest / this.instrumentDetail.currentPrice)
    };
  }

  get inputAmountExceedsBalance (): boolean {
    return this.selectedTransactionUnit === DrivewealthTransactionUnit.ByShare ?
      ((this.amountShares * this.instrumentDetail.currentPrice) > this.availableToInvest) :
      (this.data.amountUsd > this.availableToInvest);
  }

  get insufficientFundsForBuy (): boolean {
    return this.minAmount > this.availableToInvest;
  }

  get hasAmountError (): boolean {
    return this.errors.has("amount") || (this.insufficientFundsForBuy && this.selectedTransactionUnit === DrivewealthTransactionUnit.ByAmount);
  }

  get hasShareNumberError (): boolean {
    return this.errors.has("shares") || (this.insufficientFundsForBuy  && this.selectedTransactionUnit === DrivewealthTransactionUnit.ByShare);
  }

  get amountErrorText (): string {
    if (this.insufficientFundsForBuy) {
      return "You don't have enough money to invest right now";
    }
    return this.errors.first("amount");
  }

  get shareNumberErrorText (): string {
    if (this.insufficientFundsForBuy) {
      return "You don't have enough money to invest right now";
    }
    return this.errors.first("shares");
  }

  onCancel (): void {
    this.navigateBack();
  }

  navigateBack (): void {
    window.history.length > 1 ? this.$router.go(-1) : this.$router.push("/");
  }

  navigateOnBuyRequested (): void {
    this.$router.push({
      name: SharedRoutes.DrivewealthInvestmentAccountV2,
      params: { accountId: `${this.accountId}`},
      query: { force: "true" }
    });
  }

  onConfirm (): void {
    if (this.portalFunctionalityDisabled) {
      return;
    }

    if (this.data.isSubmitting) {
      return;
    }

    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }

      this.data.isSubmitting = true;
      this.onConfirmAndValidated();
    });
  }

  onConfirmAndValidated (): void {
    if (this.portalFunctionalityDisabled) {
      return;
    }

    let orderPromise: Promise<any>;

    if (this.selectedTransactionUnit === DrivewealthTransactionUnit.ByShare) {
      const formattedAmount = (this.amountShares * this.instrumentDetail.currentPrice).toFixed(2);
      this.data.amountUsd = parseFloat(formattedAmount);
    }

    if (this.isManagedAccount) {
      orderPromise = this.drivewealthStore.createManagedDepositByAccount(
          { amount: this.data.amountUsd, accountId: this.accountId });
    } else {
      orderPromise = this.drivewealthStore.createOrderRequestByAccount({
        data: {
          side: "BUY",
          units: "USD",
          stockSymbol: this.symbol,
          amount: this.data.amountUsd
        },
        accountId: this.accountId
      });
    }

    orderPromise.then(() => {
      let message = `Purchase of ${formatCurrency(this.data.amountUsd)} ${this.symbol} sent`
          + "<div>It may take up to 2 minutes for the order to display.</div>";

      if (this.selectedTransactionUnit === DrivewealthTransactionUnit.ByShare) {
        message = `Purchase of ${this.amountShares} shares ${this.symbol} sent`
            + "<div>It may take up to 2 minutes for the order to display.</div>";
      }

      this.notifierStore.setSuccess(message);
      this.navigateOnBuyRequested();
    }).finally(() => {
      this.data.isSubmitting = false;
    });
  }
}
